import {useEffect, useRef} from "react";
import {useLocation, useNavigate} from "react-router-dom";
import {Hub} from "aws-amplify/utils";
import {consumePendingInviteId, joinPath, loginPathForLocation} from "../utils/inviteRedirect";

/**
 * Routes the user on auth events without losing the business invite they
 * arrived with.
 *
 * Two paths lose an invite if left alone:
 *  - Signing out (or a token refresh failure) mid-flow used to redirect to a
 *    bare "/login", dropping the inviteId and landing the user on the
 *    dashboard afterwards with no business attached.
 *  - The Cognito hosted UI returns to a fixed redirectSignIn URL, so the only
 *    things that survive are the customState Amplify replays on the Hub and
 *    the copy stashed in sessionStorage.
 */
export default function useInviteAwareAuthRedirect() {
    const navigate = useNavigate();
    const location = useLocation();

    // Hub callbacks fire outside React's render cycle, so the current location
    // is read through a ref instead of a closure captured at subscribe time.
    const locationRef = useRef(location);
    locationRef.current = location;

    useEffect(() => {
        // Subscribed once. Calling Hub.listen from a render body leaked a
        // listener per render, so one sign-out fired several redirects.
        return Hub.listen('auth', ({payload}) => {
            switch (payload.event) {
                case "customOAuthState":
                    if (payload.data) {
                        consumePendingInviteId();
                        navigate(joinPath(payload.data), {replace: true});
                    }
                    break;
                case "signedOut":
                case "tokenRefresh_failure":
                    navigate(loginPathForLocation(locationRef.current));
                    break;
                default:
                    break;
            }
        });
    }, [navigate]);

    useEffect(() => {
        // Closes the race where customOAuthState fires before the listener
        // above is attached. Consume-once, so an abandoned invite cannot keep
        // hijacking later page loads.
        const pendingInviteId = consumePendingInviteId();
        if (pendingInviteId) {
            navigate(joinPath(pendingInviteId), {replace: true});
        }
    }, []);
}
